import { useEffect, useState } from 'react'
import {
  HiOutlineFlag,
  HiOutlineUserCircle,
  HiOutlineQuestionMarkCircle,
  HiChevronDown,
  HiCheck,
  HiXMark,
} from 'react-icons/hi2'

// ────────────────────────────────────────────────────────────────
// FilterDrawer — right-side slide-out panel matching the Figma
// design. Used by /leads (server-driven filters via URL) and
// /stages (client-side filters via local state). The parent owns
// committed state; the drawer keeps its own draft while open and
// only calls onApply when the user clicks "Filter".
// ────────────────────────────────────────────────────────────────

export type FiltersState = {
  priority: Array<'hot' | 'warm' | 'cold'>
  // Map of event_step_id → selected option_ids. Keys are strings to
  // match Rails' params[:answers] hash shape and survive JSON round-trip.
  answers: Record<string, number[]>
  assignee_ids: number[]
}

export type FilterFacet = {
  id: number
  text: string
  options: Array<{ id: number; label: string }>
}

export type AssigneeOption = {
  id: number
  name: string
}

type Props = {
  open: boolean
  onClose: () => void
  onApply: (next: FiltersState) => void
  value: FiltersState
  facets: FilterFacet[]
  assignees: AssigneeOption[]
}

export const EMPTY_FILTERS: FiltersState = {
  priority: [],
  answers: {},
  assignee_ids: [],
}

export function filtersActiveCount(f: FiltersState): number {
  return (
    f.priority.length +
    Object.values(f.answers).reduce((acc, ids) => acc + ids.length, 0) +
    f.assignee_ids.length
  )
}

// Same pill colors as PriorityBadge — selected state only. Unselected
// pills are outlined white.
const PRIORITY_STYLES: Record<FiltersState['priority'][number], { bg: string; text: string }> = {
  hot:  { bg: '#F344DF', text: '#510041' },
  warm: { bg: '#FFC940', text: '#553600' },
  cold: { bg: '#5BE8F5', text: '#00363F' },
}

const PRIORITY_LABELS: Record<FiltersState['priority'][number], string> = {
  hot: 'Hot',
  warm: 'Warm',
  cold: 'Cold',
}

const PRIORITIES: FiltersState['priority'] = ['hot', 'warm', 'cold']

export default function FilterDrawer({
  open,
  onClose,
  onApply,
  value,
  facets,
  assignees,
}: Props) {
  // Draft state — only committed via onApply. Reset from `value` each
  // time the drawer opens so reopening after cancel shows the last
  // applied state.
  const [draft, setDraft] = useState<FiltersState>(value)

  useEffect(() => {
    if (open) setDraft(value)
  }, [open, value])

  // Close on Escape.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // Lock body scroll while the drawer is open.
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  const togglePriority = (p: FiltersState['priority'][number]) => {
    setDraft((d) => ({
      ...d,
      priority: d.priority.includes(p) ? d.priority.filter((x) => x !== p) : [...d.priority, p],
    }))
  }

  const toggleAnswer = (stepId: number, optionId: number) => {
    setDraft((d) => {
      const key = String(stepId)
      const current = d.answers[key] || []
      const next = current.includes(optionId)
        ? current.filter((id) => id !== optionId)
        : [...current, optionId]
      const nextAnswers = { ...d.answers }
      if (next.length === 0) delete nextAnswers[key]
      else nextAnswers[key] = next
      return { ...d, answers: nextAnswers }
    })
  }

  const toggleAssignee = (userId: number) => {
    setDraft((d) => ({
      ...d,
      assignee_ids: d.assignee_ids.includes(userId)
        ? d.assignee_ids.filter((id) => id !== userId)
        : [...d.assignee_ids, userId],
    }))
  }

  const clearAll = () => setDraft(EMPTY_FILTERS)

  const apply = () => {
    onApply(draft)
    onClose()
  }

  return (
    <div
      className={`fixed inset-0 z-50 ${open ? '' : 'pointer-events-none'}`}
      aria-hidden={!open}
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close filter drawer"
        onClick={onClose}
        className={`absolute inset-0 bg-black/40 transition-opacity ${
          open ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {/* Panel */}
      <aside
        role="dialog"
        aria-label="Filters"
        className={`absolute right-0 top-0 flex h-full w-full max-w-[440px] flex-col bg-[#EFEFEF] shadow-xl transition-transform duration-200 ease-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header (dark, sticky) */}
        <header className="flex items-center gap-3 bg-ink px-5 py-5">
          <h2 className="flex-1 font-display text-base font-medium text-white">Filter</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
            aria-label="Close"
          >
            <HiXMark className="h-5 w-5" />
          </button>
        </header>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <PrioritySection selected={draft.priority} onToggle={togglePriority} />

          {facets.map((facet) => (
            <CheckboxSection
              key={facet.id}
              icon={<HiOutlineQuestionMarkCircle className="h-4 w-4" />}
              title={facet.text}
              items={facet.options.map((opt) => ({
                id: opt.id,
                label: opt.label,
                checked: (draft.answers[String(facet.id)] || []).includes(opt.id),
              }))}
              onToggle={(optionId) => toggleAnswer(facet.id, optionId)}
            />
          ))}

          {assignees.length > 0 && (
            <CheckboxSection
              icon={<HiOutlineUserCircle className="h-4 w-4" />}
              title="Assignee"
              items={assignees.map((u) => ({
                id: u.id,
                label: u.name,
                checked: draft.assignee_ids.includes(u.id),
              }))}
              onToggle={toggleAssignee}
            />
          )}
        </div>

        {/* Footer (sticky bottom) */}
        <footer className="flex items-center justify-end gap-2 border-t border-gray-200 bg-[#EFEFEF] px-5 py-4">
          <button
            type="button"
            onClick={clearAll}
            className="rounded-md border border-gray-300 bg-white px-5 py-3 font-display text-base font-medium text-ink transition hover:bg-gray-50"
          >
            Clear All
          </button>
          <button
            type="button"
            onClick={apply}
            className="rounded-md bg-ink px-7 py-3 font-display text-base font-medium text-white transition hover:bg-midnight-green"
          >
            Filter
          </button>
        </footer>
      </aside>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────
// PrioritySection — pill buttons (not checkboxes). Single caller.
// ────────────────────────────────────────────────────────────────

function PrioritySection({
  selected,
  onToggle,
}: {
  selected: FiltersState['priority']
  onToggle: (p: FiltersState['priority'][number]) => void
}) {
  return (
    <section className="mb-5 flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <HiOutlineFlag className="h-4 w-4 text-ink" />
        <h3 className="font-display text-base font-medium text-ink">Lead Priority</h3>
      </div>
      <div className="flex flex-wrap gap-2">
        {PRIORITIES.map((p) => {
          const isSelected = selected.includes(p)
          const style = PRIORITY_STYLES[p]
          return (
            <button
              key={p}
              type="button"
              onClick={() => onToggle(p)}
              className="rounded-full border px-5 py-1.5 font-display text-sm transition"
              style={
                isSelected
                  ? { backgroundColor: style.bg, borderColor: style.bg, color: style.text }
                  : { backgroundColor: '#F8FAFC', borderColor: '#D1D5DB', color: '#111D1E' }
              }
            >
              {PRIORITY_LABELS[p]}
            </button>
          )
        })}
      </div>
    </section>
  )
}

// ────────────────────────────────────────────────────────────────
// CheckboxSection — collapsible section with divider'd checkbox
// list. Used for choice questions and the assignee group.
// ────────────────────────────────────────────────────────────────

function CheckboxSection({
  icon,
  title,
  items,
  onToggle,
}: {
  icon: React.ReactNode
  title: string
  items: Array<{ id: number; label: string; checked: boolean }>
  onToggle: (id: number) => void
}) {
  const [open, setOpen] = useState(true)

  return (
    <section className="mb-5 flex flex-col gap-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2"
      >
        <span className="text-ink">{icon}</span>
        <h3 className="flex-1 text-left font-display text-base font-medium text-ink">{title}</h3>
        <HiChevronDown
          className={`h-4 w-4 text-ink transition-transform ${open ? '' : '-rotate-90'}`}
        />
      </button>

      {open && (
        <ul className="flex flex-col border-t border-gray-200">
          {items.map((item) => (
            <li key={item.id} className="border-b border-gray-200">
              <button
                type="button"
                onClick={() => onToggle(item.id)}
                className="flex w-full items-center gap-3 py-2 text-left"
              >
                <span className="flex-1 font-display text-sm text-ink">{item.label}</span>
                <span
                  className={`flex h-4 w-4 items-center justify-center rounded border ${
                    item.checked
                      ? 'border-ink bg-ink text-white'
                      : 'border-gray-300 bg-white'
                  }`}
                  aria-hidden="true"
                >
                  {item.checked && <HiCheck className="h-3 w-3" strokeWidth={3} />}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
